export const GROWTH_ENTRIES = ['home', 'landing'];
export const GROWTH_PLATFORMS = ['ios', 'android'];
export const STORE_METRICS = {
  ios: ['apple_first_time_downloads', 'apple_product_page_views'],
  android: ['google_install_events', 'google_store_listing_visitors'],
};
export const DAILY_VISIT_METRICS = new Set(['google_store_listing_visitors', 'apple_product_page_views']);

export function growthToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

export function getGrowthDateRange(days = 30, now = new Date()) {
  const end = new Date(`${growthToday(now)}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - days + 1);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

function validDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value || '') &&
    Number.isFinite(Date.parse(`${value}T00:00:00Z`)) &&
    new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

export function validateGrowthRange(from, to, now = new Date()) {
  if (!validDate(from) || !validDate(to)) return { valid: false, errorKey: 'analytics.growth.rangeInvalid' };
  const days = (Date.parse(to) - Date.parse(from)) / 86400000 + 1;
  if (days < 1 || days > 93) return { valid: false, errorKey: 'analytics.growth.rangeLimit' };
  if (to > getGrowthDateRange(1, now).to) return { valid: false, errorKey: 'analytics.growth.rangeFuture' };
  return { valid: true, errorKey: null };
}

// URL scope is explicit: invalid shared dates never silently query another period.
export function readGrowthView(search = '', now = new Date()) {
  const params = new URLSearchParams(search);
  const seven = getGrowthDateRange(7, now);
  const thirty = getGrowthDateRange(30, now);
  const fallback = params.get('range') === '30d' ? thirty : seven;
  const hasDates = params.has('from') || params.has('to');
  const range = hasDates ? { from: params.get('from') || '', to: params.get('to') || '' } : fallback;
  const requestedPlatform = (params.get('platform') || 'all').toLowerCase();
  const invalidPlatform = !['all', ...GROWTH_PLATFORMS].includes(requestedPlatform);
  // Retired combined links open the landing report; home is always separate.
  const requestedEntry = params.get('entry') === 'all' ? 'landing' : params.get('entry') ?? 'landing';
  const invalidEntry = !GROWTH_ENTRIES.includes(requestedEntry);
  const validation = validateGrowthRange(range.from, range.to, now);
  const matches = (candidate) => candidate.from === range.from && candidate.to === range.to;
  return {
    range,
    entry: invalidEntry ? 'landing' : requestedEntry,
    platform: invalidPlatform ? 'all' : requestedPlatform,
    preset: matches(seven) ? '7d' : matches(thirty) ? '30d' : 'custom',
    errorKey: invalidPlatform ? 'analytics.growth.platformInvalid' : invalidEntry ? 'analytics.growth.entryInvalid' : validation.errorKey,
    versionRequested: Boolean(params.get('version') || params.get('appVersion')),
  };
}

export function growthViewSearch(search, range, platform, preset, entry) {
  const params = new URLSearchParams(search);
  const version = params.get('version') || params.get('appVersion');
  if (version) params.set('version', version);
  params.delete('appVersion');
  params.set('from', range.from);
  params.set('to', range.to);
  params.set('platform', platform);
  params.set('range', preset);
  if (entry != null) params.set('entry', entry);
  return `?${params.toString()}`;
}

export function dateSpine(from, to) {
  if (!validDate(from) || !validDate(to)) return [];
  const rows = [];
  for (const date = new Date(`${from}T00:00:00Z`); date <= new Date(`${to}T00:00:00Z`); date.setUTCDate(date.getUTCDate() + 1)) {
    rows.push(date.toISOString().slice(0, 10));
  }
  return rows;
}

export function metricNumber(metric) {
  return metric?.status === 'ok' && typeof metric.value === 'number' && Number.isFinite(metric.value) && metric.value >= 0
    ? metric.value : null;
}

export function storeMetric(data, platform, metricId) {
  if (DAILY_VISIT_METRICS.has(metricId)) {
    const candidates = [...(data?.daily || [])].filter((row) => row.platform === platform &&
      row.metrics?.some((metric) => metric.metric_id === metricId))
      .sort((a, b) => b.date.localeCompare(a.date));
    const latestValid = candidates.find((row) => metricNumber(row.metrics.find((metric) => metric.metric_id === metricId)) != null);
    const latest = latestValid || candidates[0];
    const summaryMetric = data?.summary?.find((row) => row.platform === platform)?.metrics?.find((item) => item.metric_id === metricId);
    let metric = latest?.metrics.find((item) => item.metric_id === metricId);
    const reportId = metric?.report_id || summaryMetric?.report_id;
    const report = data?.reports?.find((item) => item.report_id === reportId);
    if (!metric) {
      // Summary uniqueness limits do not describe daily report availability.
      metric = { metric_id: metricId, value: null, status: report?.status === 'ok' ? 'not_collected' : report?.status || 'not_collected', aggregation_basis: 'reported_daily', report_id: reportId };
    }
    if (!latestValid && ['not_configured', 'not_collected', 'permission_denied', 'unavailable'].includes(report?.status)) {
      // Missing date-spine cells can be age-labelled delayed before collection ever ran.
      metric = { ...metric, value: null, status: report.status };
    }
    // A date-spine row marks a requested day; it is not evidence that a report exists.
    const date = latestValid ? latest.date : metric?.covered_to || report?.latest_available_date || undefined;
    return { metric, date, daily: true };
  }
  const metric = data?.summary?.find((row) => row.platform === platform)?.metrics?.find((item) => item.metric_id === metricId);
  if (metricNumber(metric) != null || metric?.aggregation_basis !== 'sum_daily_counts') return { metric, daily: false };
  const dates = dateSpine(data?.range?.from, data?.range?.to);
  const confirmed = [];
  for (const date of dates) {
    const matches = (data?.daily || []).filter((row) => row.platform === platform && row.date === date)
      .flatMap((row) => row.metrics || []).filter((item) => item.metric_id === metricId && item.report_id === metric.report_id);
    // A duplicate date is ambiguous; never count it twice or pick an arbitrary row.
    if (matches.length === 1 && Number.isSafeInteger(metricNumber(matches[0]))) confirmed.push({ date, value: matches[0].value });
  }
  const value = confirmed.reduce((sum, row) => sum + row.value, 0);
  if (!confirmed.length || !Number.isSafeInteger(value)) return { metric, daily: false };
  return {
    metric: { ...metric, value, status: 'ok', covered_from: confirmed[0].date, covered_to: confirmed.at(-1).date },
    daily: false,
    coverage: { days: confirmed.length, excluded: dates.length - confirmed.length },
  };
}

export function growthChartRows(from, to, store) {
  return dateSpine(from, to).map((date) => {
    const row = { date };
    GROWTH_PLATFORMS.forEach((platform) => {
      const daily = store?.daily?.find((item) => item.date === date && item.platform === platform);
      STORE_METRICS[platform].forEach((metricId, index) => {
        row[`${platform}${index === 0 ? 'Downloads' : 'Visits'}`] = metricNumber(daily?.metrics?.find((metric) => metric.metric_id === metricId));
      });
    });
    return row;
  });
}

export function queryStatus(query) {
  if (query.isPending || query.isLoading) return 'loading';
  if (query.error?.status === 403) return 'permission_denied';
  if (query.error) return 'unavailable';
  return 'ok';
}
